import { AfterViewInit, Component, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScoreLocalService } from '../../pages/score/score.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NavigationTrackingService, NavigationTriggerType } from '../../services/navigation-tracking.service';
import { NovaCompanionFabComponent } from '../nova-companion-fab/nova-companion-fab.component';

import { FlipCoachTargetDirective } from '../coach-mark/coach-mark-target.directive';
import { CM01CommunityWelcomeService } from '../../services/onboarding-lessons/cm01-community-welcome.service';

@Component({
  selector: 'app-bottom-nav',
  templateUrl: './bottom-nav.component.html',
  styleUrls: ['./bottom-nav.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, AngularSvgIconModule, NovaCompanionFabComponent, FlipCoachTargetDirective],
})
export class BottomNavComponent implements AfterViewInit, OnDestroy {
  /**
   * FLIP-453: the rendered height of the Nova "Ask Nova" tab stacked above the nav (FLIP-316) -
   * the height of `app-nova-companion-fab` itself - or 0 when it is absent, or is the fixed round
   * launcher (which takes no room in this flow). Published on :root so the shared page clearance
   * (`--bottom-nav-height-padding`, _common.scss) can add it to its nav allowance. The tab only,
   * not the host: the nav's own box (50px + its padding) is already what that allowance covers.
   */
  static readonly TAB_HEIGHT_VAR = '--bottom-nav-tab-height';

  private subscriptions = new Subscription();
  private activeRoute: string;
  private tabObserver?: ResizeObserver;

  constructor(
    private host: ElementRef<HTMLElement>,
    private router: Router,
    private scoreLocalService: ScoreLocalService,
    private navigationTracking: NavigationTrackingService,
    protected communityWelcome: CM01CommunityWelcomeService,
  ) {
    this.activeRoute = this.currentPath();
    const sub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.activeRoute = this.currentPath();
      });
    this.subscriptions.add(sub);
  }

  ngAfterViewInit(): void {
    const fab = this.host.nativeElement.querySelector<HTMLElement>('app-nova-companion-fab');
    if (!fab || typeof ResizeObserver === 'undefined') {
      return; // the clearance falls back to its nav-only value
    }
    const root = document.documentElement.style;
    this.tabObserver = new ResizeObserver(() => {
      root.setProperty(
        BottomNavComponent.TAB_HEIGHT_VAR,
        `${Math.round(fab.getBoundingClientRect().height)}px`,
      );
    });
    this.tabObserver.observe(fab);
  }

  private currentPath(): string {
    return this.router.url.split('?')[0].split('#')[0];
  }

  isRouteActive(path: string, exact = false): boolean {
    return exact ? this.activeRoute === path : this.activeRoute === path || this.activeRoute.startsWith(`${path}/`);
  }

  /** Fires a BottomNav*Clicked NavigationEvent. `toScreen` is the routerLink target path. */
  trackNavItem(toScreen: string, eventName: string): void {
    this.navigationTracking.track({
      fromScreen: this.activeRoute,
      toScreen,
      triggerType: NavigationTriggerType.ViaNavigation,
      eventName,
    });
  }

  navigateScore(): void {
    const fromScreen = this.activeRoute;
    const sub = this.scoreLocalService.getLastScore().subscribe(() => {
      const route = this.scoreLocalService.shouldShowMarginOfSafety()
        ? '/score/marginofsafety'
        : '/score';
      this.navigationTracking.track({
        fromScreen,
        toScreen: route,
        triggerType: NavigationTriggerType.ViaNavigation,
        eventName: 'BottomNavScoreClicked',
      });
      this.router.navigate([route]);
    });
    this.subscriptions.add(sub);
  }

  isScoreActive(): boolean {
    return this.router.url.startsWith('/score') || this.isRouteActive('/history');
  }

  isHomeActive(): boolean {
    return this.isRouteActive('/landing-page', true) || this.isRouteActive('/brainbites');
  }

  isPlanActive(): boolean {
    return this.isRouteActive('/plan-home') || this.isRouteActive('/plan') || this.isRouteActive('/card-library');
  }

  ngOnDestroy(): void {
    this.tabObserver?.disconnect();
    document.documentElement.style.removeProperty(BottomNavComponent.TAB_HEIGHT_VAR);
    this.subscriptions.unsubscribe();
  }
}
